import { createAsyncThunk } from '@reduxjs/toolkit';
import { createFetch, createSchema } from '@better-fetch/fetch';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

import {
  ApiError,
  ApiErrorSchema,
  DataItemEnabledCondition,
  DataItemType,
  FileBrowserPage,
  NavigateEventContext,
  RucioLocationsType,
  Site,
  StorageArea,
  ToolType,
  toolTypeToPath,
} from '../../utils/types';
import {
  Event,
  EventType,
  EventTarget,
  DataCollection,
  InitiateDMSearchParams,
  MoveDataToStorage,
  AddCollectionEvent,
} from '../../utils/events';
import { ContextMenuItem } from '../menus/context-menu';

// functions
import { useTranslation } from 'react-i18next';
import { APIPrefix, siteName } from '../../utils/functions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { store } from '../../app/store';

// import state definition and functions.
import {
  ContextMenuState,
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_FILE_BROWSER,
  CONTEXT_MENU_FILE_BROWSER_STAGING,
  ctx_setContextMenuDefinition,
} from '../menus/context-menu-slice';
import {
  FileBrowserState,
  RenameType,
  restorePageParams,
  savePageParams,
  setParametersMaximised,
  setDetailsMaximised,
  setComputeMaximised,
  setFilenameFilter,
  setFocusElement,
  setLifetimeStr,
  setLifetime,
  setShowHiddenFiles,
  setSelectedItems,
  setStoragePath,
  setDisplayComputePanel,
  setDataList,
  setPageDisplayed,
  setNamespace,
  setDataset,
  setJobID,
  setCollection,
  setSelectedDivs,
  setLoadingComponent,
  setLatestQueryID,
  setErrorMessage,
  setVosUri,
  setRenameIndex,
  setRenameType,
  setRenameFilename,
  setClipboard,
  setClipboardPath,
  setInClipboardFolder,
  setOpenFileDialogTrigger,
  setContextMenuRefresh,
} from './file-browser-slice';
import { GlobalState, rG_setSubmittingJob } from '../../app/global-slice';
import { DataState } from '../../app/data-slice';
import {
  fnD_countAvailableFiles,
  fnD_getAvailableLocations,
} from '../../app/data-actions';
import { handleEvent } from '../../app-actions';
import { setSelectedTool } from '../../app-slice';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C O N S T A N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  F U N C T I O N S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  If the chooses to add an item that requires a name, we
//  should create a temporary item and set the renaming index
//  to this new item.
//
//  ------------------------------------------------------------

export function addTemporaryItem() {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;

  // add temporary item.
  var dataList = state.dataList.slice();
  const newItem: DataItemType = {
    id: -1,
    namespace: '',
    filename: '',
    did_type: 'directory',
    bytes: 0,
  };
  dataList.push(newItem);

  // update state.
  store.dispatch(setDataList(dataList));
  store.dispatch(setRenameType(RenameType.NEW));
  store.dispatch(setRenameFilename('New folder'));
  store.dispatch(setRenameIndex(dataList.length - 1));
  //
} // addTemporaryItem

//  ------------------------------------------------------------
//
//  Rebuild the context menu for staging selected items.
//
//  ------------------------------------------------------------

export function buildContextMenu(args: {
  menuID: CONTEXT_MENU_FILE_BROWSER;
  parentRowID: number;
}) {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;
  const stateData: DataState = stateFetch.data;

  // store the items so we can return them from the function.
  let items: ContextMenuItem[] = [];

  // rebuild the main menu.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER.MAIN) {
    //
    // define a separator.
    const separator: ContextMenuItem = {
      id: '',
      text: '---',
      childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
    };

    // move to is always there.
    const moveTo: ContextMenuItem = {
      id: 'mnu-main-moveto',
      text: 'Stage to',
      childMenu: CONTEXT_MENU_FILE_BROWSER.SITES,
      onClick: false,
      enabled: state.selectedDivs.length > 0,
    };
    items.push(moveTo);

    // add other items depending on which page we are on.
    if (
      state.pageParams.pageDisplayed === FileBrowserPage.Collections ||
      state.pageParams.pageDisplayed === FileBrowserPage.Collection
    ) {
      items.push(separator);
      const deleteCollection: ContextMenuItem = {
        id: 'mnu-main-deletecollection',
        text: 'Delete collection(s)',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(deleteCollection);
      const deleteItem: ContextMenuItem = {
        id: 'mnu-main-removeitemfromcollection',
        text: 'Remove item(s) from collection',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(deleteItem);
    }
    if (
      state.pageParams.pageDisplayed === FileBrowserPage.UserStorage ||
      state.pageParams.pageDisplayed === FileBrowserPage.ProjectStorage
    ) {
      items.push(separator);
      const createFolder: ContextMenuItem = {
        id: 'mnu-main-createfolder',
        text: 'Create folder',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(createFolder);
      const renameItem: ContextMenuItem = {
        id: 'mnu-main-renamefile',
        text: 'Rename',
        shortcut: 'F2',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(renameItem);
      const deleteNode: ContextMenuItem = {
        id: 'mnu-main-deletenode',
        text: 'Delete',
        shortcut: 'Del',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(deleteNode);
      items.push(separator);
      const copyItem: ContextMenuItem = {
        id: 'mnu-main-copy',
        text: 'Copy',
        shortcut: 'Ctrl+C',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(copyItem);
      const cutItem: ContextMenuItem = {
        id: 'mnu-main-cut',
        text: 'Cut',
        shortcut: 'Ctrl+X',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(cutItem);
      const pasteItem: ContextMenuItem = {
        id: 'mnu-main-paste',
        text: 'Paste',
        shortcut: 'Ctrl+V',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(pasteItem);
      items.push(separator);
      const uploadItem: ContextMenuItem = {
        id: 'mnu-main-upload',
        text: 'Upload file',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(uploadItem);
      const downloadItem: ContextMenuItem = {
        id: 'mnu-main-download',
        text: 'Download file',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: false,
      };
      items.push(downloadItem);
    }

    // update the context menu definition.
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // update the sites menu.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER.SITES) {
    //
    items = stateData.storageAreas.map((item) => ({
      id:
        'menu-node-' + item.site.node?.toLowerCase() ??
        '' + '-site-' + item.site.site?.toLowerCase() ??
        '',
      text: siteName({ site: item.site }),
      childMenu: CONTEXT_MENU_FILE_BROWSER.STORAGES,
      onClick: false,
      enabled: true,
    }));
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // if we've entered a menu item on the sites context menu then
  // rebuild the storage areas menu.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER.STORAGES) {
    //
    // update the state.
    items = stateData.storageAreas[args.parentRowID].storage_areas.map(
      (item: StorageArea) => ({
        id: 'menu-storage-' + item.storage_id,
        text: item.name !== '' ? item.name : '<' + item.relative_path + '>',
        childMenu: CONTEXT_MENU_FILE_BROWSER.NONE,
        onClick: true,
        enabled: true,
      }),
    );
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // refresh the menu.
  store.dispatch(setContextMenuRefresh(Date.now()));

  // return the items.
  return items;
  //
} // buildContextMenu

//  ------------------------------------------------------------
//
//  Rebuild the context menu for staging selected items.
//
//  ------------------------------------------------------------

export function buildContextMenuFileBrowserDataFiles(args: {
  menuID: CONTEXT_MENU_FILE_BROWSER_STAGING;
  parentRowID: number;
}) {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;
  const stateData: DataState = stateFetch.data;

  // store the items so we can return them from the function.
  let items: ContextMenuItem[] = [];

  // rebuild the main menu.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER_STAGING.MAIN) {
    //
    // define a separator.
    const separator: ContextMenuItem = {
      id: '',
      text: '---',
      childMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.NONE,
    };

    // move to is always there.
    const moveTo: ContextMenuItem = {
      id: 'mnu-main-moveto',
      text: 'Stage to',
      childMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.SITES,
      onClick: false,
      enabled: state.selectedDivs.length > 0,
    };
    items.push(moveTo);

    // update the context menu definition.
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // update the sites menu.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER_STAGING.SITES) {
    //
    items = stateData.storageAreas.map((item) => ({
      id:
        'menu-node-' + item.site.node?.toLowerCase() ??
        '' + '-site-' + item.site.site?.toLowerCase() ??
        '',
      text: siteName({ site: item.site }),
      childMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.STORAGES,
      onClick: false,
      enabled: true,
    }));
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // if we've entered a menu item on the sites context menu then
  // rebuild the storage areas menu.CONTEXT_MENU_FILE_BROWSER_STAGING
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER_STAGING.STORAGES) {
    //
    // update the state.
    items = stateData.storageAreas[args.parentRowID].storage_areas.map(
      (item: StorageArea) => ({
        id: 'menu-storage-' + item.storage_id,
        text: item.name !== '' ? item.name : '<' + item.relative_path + '>',
        childMenu: CONTEXT_MENU_FILE_BROWSER_STAGING.NONE,
        onClick: true,
        enabled: true,
      }),
    );
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER_STAGING,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // refresh the menu.
  store.dispatch(setContextMenuRefresh(Date.now()));

  // return the items.
  return items;
  //
} // buildContextMenuFileBrowserDataFiles

//  ------------------------------------------------------------
//
//  Cancels the current search operation.
//
//  ------------------------------------------------------------

export function cancelQuery() {
  //
  // get state.
  const state: any = store.getState();

  // set the latest query ID to -1.
  store.dispatch(setLoadingComponent(false));
  store.dispatch(setLatestQueryID(-1));
  store.dispatch(restorePageParams());
  //
} // cancelQuery

//  ------------------------------------------------------------
//
//  An asynchronous function that deletes a single file or
//  folder in user storage.
//
//  ------------------------------------------------------------

export const deleteFile = createAsyncThunk(
  'file_browser/deleteFile',
  async (
    args: {
      filename: string;
      storageType?: 'user' | 'project';
    },
    thunkAPI,
  ) => {
    //
    const stateFetch: any = thunkAPI.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;
    const globalState: GlobalState = stateFetch.global;

    setLoadingComponent(true);

    const { filename, storageType = 'user' } = args;

    const path = state.pageParams.storagePath?.join('/') ?? '';

    const url = globalState.selectedCanfar?.url ?? '';
    const username = globalState.preferredUsername ?? '';
    const project_name = globalState.project?.name ?? '';
    const basePrefix =
      storageType === 'user' ? `/home/${username}` : `/projects/${project_name}`;

    const filePath = `${basePrefix}/${path}/${filename}`;

    const urlCommand =
      `${APIPrefix()}/v1/storage/delete_item` +
      `?url=${encodeURIComponent(url)}` +
      `&path=${encodeURIComponent(filePath)}`;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.ok) {
        setLoadingComponent(false);
        refreshPage();
      } else {
        let rawJson = await apiResult.text();
        const parsedJson = JSON.parse(rawJson, (key, value) => {
          return value;
        });
        console.log('Failed to create directory:', parsedJson.detail);
        setLoadingComponent(false);
        /*if (parsedJson.detail.includes('403 Forbidden')) {
          toast.error('Request forbidden by administrative rules.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: sG_DarkMode ? 'dark' : 'light',
          });
        }*/ // CJS: temporarily removed
      }
    } catch (e) {
      console.error('Error deleting file:', e);
      setLoadingComponent(false);
      refreshPage();
    }
    //
  },
); // deleteFile

//  ------------------------------------------------------------
//
//  Display the file browser root page.
//
//  ------------------------------------------------------------

async function _displayFileBrowserRoot(args: { focus?: boolean }) {
  //
  console.log('displayFileBrowserRoot fires');

  var dataList: DataItemType[] = [];

  var gatewayAdminGroupFound: boolean = false;

  // check if the user is authorised to browse namespaces.
  var urlCommand: string = APIPrefix() + '/v1/list_groups';

  try {
    //
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Return code 200 means the API has run.
    if (apiResult.status === 200) {
      const returnedJson = await apiResult.json();
      if (returnedJson.groups !== undefined) {
        gatewayAdminGroupFound =
          returnedJson.groups.indexOf('services/gateway-backend-api/roles/admin') > -1;
      }
    }
    //
  } catch (e) {}

  // FIXME: tmp: force the admin group to be true so we can browse namespaces.
  gatewayAdminGroupFound = true;

  // initialise the data list with just user storage.
  var dataList: DataItemType[] = [
    {
      id: 0,
      namespace: '',
      filename: 'User storage',
      did_type: 'user_storage',
      bytes: -1,
      enabled: DataItemEnabledCondition.SELECTED_CANFAR,
    },
  ];

  // add project storage
  dataList.push({
    id: 1,
    namespace: '',
    filename: 'Project storage',
    did_type: 'project_storage',
    bytes: -1,
  });

  // add namspaces.
  if (gatewayAdminGroupFound === true) {
    dataList.push({
      id: 2,
      namespace: '',
      filename: 'Namespaces',
      did_type: 'namespace',
      bytes: -1,
    });
  }

  // add collections.
  dataList.push({
    id: 3,
    namespace: '',
    filename: 'Collections',
    did_type: 'collection',
    bytes: -1,
  });

  // update the state.
  store.dispatch(setDataList(dataList));

  // update the focus trigger if we need to be setting the focus.
  if (args.focus === true) {
    store.dispatch(setFocusElement({ divID: 0 }));
  }
  //
} // _displayFileBrowserRoot

//  ------------------------------------------------------------
//
//  Build the data list from a list of namespaces.
//
//  ------------------------------------------------------------

function _displayNamespaces(args: {
  namespaces: string[];
  filename: string | undefined;
}) {
  //
  var dataList: DataItemType[] = [];

  for (var i = 0; i < args.namespaces.length; i++) {
    const namespace: DataItemType = {
      id: -1,
      namespace: '',
      filename: args.namespaces[i],
      did_type: 'namespace',
      bytes: -1,
    };
    var addItem: boolean = true;
    if (args.filename !== undefined)
      if (args.namespaces[i].indexOf(args.filename) === -1) {
        addItem = false;
      }
    if (addItem === true) {
      dataList.push(namespace);
    }
  }

  // update the state.
  store.dispatch(setDataList(dataList));
  //
} // _displayNamespaces

//  ------------------------------------------------------------
//
//  An asynchronous function that uploads a file to user
//  storage.
//
//  ------------------------------------------------------------

export const downloadFile = createAsyncThunk(
  'file_browser/downloadFile',
  async (
    args: {
      file: DataItemType;
      storageType?: 'user' | 'project';
    },
    thunkAPI,
  ) => {
    //
    const stateFetch: any = thunkAPI.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;
    const globalState: GlobalState = stateFetch.global;

    console.log('downloadFile fires:', args.file);
    store.dispatch(setLoadingComponent(true));

    const { file, storageType = 'user' } = args;

    const path = state.pageParams.storagePath?.join('/') ?? '';

    const url = globalState.selectedCanfar?.url ?? '';
    const username = globalState.preferredUsername ?? '';
    const project_name = globalState.project?.name ?? '';

    const basePrefix =
      storageType === 'user' ? `/home/${username}` : `/projects/${project_name}`;
    const fromFile = `${basePrefix}/${path}/${file.filename}`;

    const urlCommand =
      `${APIPrefix()}/v1/storage/download_file` +
      `?url=${encodeURIComponent(url)}` +
      `&from_file=${encodeURIComponent(fromFile)}`;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'GET',
        credentials: 'include',
      });

      if (!apiResult.ok) {
        let rawJson = await apiResult.text();
        const parsedJson = JSON.parse(rawJson, (key, value) => {
          return value;
        });
        console.log('Failed to download a file:', parsedJson.detail);
        store.dispatch(setLoadingComponent(false));
        /*if (parsedJson.detail.includes('403 Forbidden')) {
          toast.error('Request forbidden by administrative rules.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: sG_DarkMode ? 'dark' : 'light',
          });
        }*/ // CJS: temporarily removed
        return;
      }

      const blob = await apiResult.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = file.filename;
      anchor.click();

      // Clean up
      window.URL.revokeObjectURL(objectUrl);
      store.dispatch(setLoadingComponent(false));
    } catch (e) {
      console.error('Error downloading file:', e);
      store.dispatch(setLoadingComponent(false));
    }
  },
  //
); // downloadFile

//  ------------------------------------------------------------
//
//  build an error message based upon the API error - used
//  when retrieving items from user or project storage.
//
//  ------------------------------------------------------------

function _extractErrorMessage(error: ApiError): string {
  //
  const { detail } = error;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((e: any) =>
        e?.msg
          ? `${e.msg} (${e.loc?.join?.('.') ?? 'unknown location'})`
          : JSON.stringify(e),
      )
      .join('\n');
  }

  return 'Unexpected Error. Please try again.';
  //
} // _extractErrorMessage

//  ------------------------------------------------------------
//
//  Handle events raised by other components.
//
//  ------------------------------------------------------------

export const eventHandler = createAsyncThunk(
  'file_browser/eventHandler',
  async (args: { event: Event; ctx?: NavigateEventContext }, thunkAPI) => {
    //
    if (
      args.event.eventTarget === EventTarget.GLOBAL ||
      args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE
    ) {
      if (args.event.eventType === EventType.INITIATE_DM_SEARCH) {
        const params = args.event.parameters as InitiateDMSearchParams;
        console.log(`Page: ${JSON.stringify(params)}`);
        if (params?.showPage) {
          thunkAPI.dispatch(setSelectedTool(ToolType.FILE_BROWSER));
          args.ctx?.navigate?.(toolTypeToPath[ToolType.FILE_BROWSER]);
        }
        thunkAPI.dispatch(initiateDMSearch(params));
      }

      if (
        args.event.eventType === EventType.ITEMS_ADDED_TO_DATA_COLLECTION ||
        args.event.eventType === EventType.ITEMS_REMOVED_FROM_DATA_COLLECTION
      ) {
        const params: AddCollectionEvent = args.event.parameters;
        const messageText =
          (params.itemsAdded !== undefined ? params.itemsAdded.toString() : '0') +
          ' item(s)' +
          (args.event.eventType === EventType.ITEMS_ADDED_TO_DATA_COLLECTION
            ? 'added'
            : 'removed') +
          ' to data collection ' +
          (params.collectionName !== undefined ? "'" + params.collectionName + "' " : '');
        args.ctx?.showSuccessToast?.(messageText);
      }

      if (
        args.event.eventType === EventType.DATA_COLLECTION_ADDED ||
        args.event.eventType === EventType.DATA_COLLECTION_DELETED
      ) {
        const project: string | undefined = args.event.parameters;
        if (project !== undefined && project !== '') {
          //
          // use the current timestamp as the query ID, and update state.
          const queryID: number = Date.now();
          store.dispatch(setLatestQueryID(queryID));
          store.dispatch(setLoadingComponent(true));
          _loadCollections({
            queryID: queryID,
          });
          //
        }
      }
      //
    }

    // Lets run a series of toast messages depending on an event
    if (
      [
        EventType.DATA_COLLECTION_ADDED,
        EventType.DATA_COLLECTION_DELETED,
        EventType.DATA_COLLECTION_ALREADY_EXISTS,
        EventType.DATA_COLLECTION_DOES_NOT_EXIST,
        EventType.USER_NOT_PROJECT_MEMBER_NEW_COLLECTION,
        EventType.USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION,
        EventType.ITEMS_ADDED_TO_DATA_COLLECTION,
      ].includes(args.event.eventType)
    ) {
      _handleToastMessages(
        args.event.eventType,
        args.event.parameters as AddCollectionEvent,
        args.ctx,
      );
    }
    //
  },
); // eventHandler

//  ------------------------------------------------------------
//
//  Returns the current filename filter from the cache.
//
//  ------------------------------------------------------------

export function filenameFilter() {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;
  const filenameFilter: string[] = state.pageParams.filenameFilter;
  //
  let filter: string = '';

  if (filenameFilter.length > 0) {
    filter = filenameFilter[filenameFilter.length - 1];
  }

  // return something.
  return filter;
  //
} // filenameFilter

//  ------------------------------------------------------------
//
//  Builds the list of sites where data in this catalog is
//  stored, along with the number of items found at that site.
//
//  The function then sorts the sites by availability, and site
//  name.
//
//  If a 'selected items' list is supplied, then we will use
//  that, otherwise we will use the 'all items' list.
//
//  ------------------------------------------------------------

export function getRucioSites(args: {
  allItems: DataItemType[];
  selectedItems?: DataItemType[];
  catalog: string;
}): {
  site: Site;
  count: number;
}[] {
  //
  // fetch the state.
  let state: any = store.getState();

  // the list of sites for ALL data in the selected project.
  const projectSites: {
    site: Site;
    count: number;
  }[] = state.data.sites

    /*
    //  filter the sites to only those that contain
    //  at least one project item.
    */
    .filter(
      (site: Site) =>
        state.data.rucioLocations.filter(
          (item: RucioLocationsType) =>
            item.catalog.indexOf(args.catalog) > -1 &&
            (args.allItems.length === 0 ||
              args.allItems.findIndex(
                (find) =>
                  find.namespace === item.namespace && find.filename === item.filename,
              ) > -1) &&
            item.availableSites.findIndex((find) => find.site_uuid === site.site_uuid) >
              -1,
        ).length > 0,
    )

    /*
    //  add the availability count
    */

    .map((site: Site) => {
      //
      return {
        site: site,
        count: fnD_countAvailableFiles({
          site: site,
          items: args.allItems,
        }),
      };
      //
    })

    /*
    //  sort the sites in order of availability.
    */

    .sort((a: { site: Site; count: number }, b: { site: Site; count: number }) =>
      a.count < b.count ||
      (siteName({ site: a.site }) > siteName({ site: b.site }) && a.count === b.count)
        ? 1
        : -1,
    );

  // the list of sites for project items that have been selected.
  const selectedItemSites: {
    site: Site;
    count: number;
  }[] =
    args.selectedItems === undefined || args.selectedItems?.length === 0
      ? []
      : state.data.sites

          /*
    // only include sites that appear in the 'project-files' catalog in the Rucio locations object.
    */

          .filter(
            (site: Site) =>
              state.data.rucioLocations.filter(
                (item: RucioLocationsType) =>
                  item.catalog.indexOf(args.catalog) > -1 &&
                  ((args.selectedItems ?? []).length === 0 ||
                    (args.selectedItems ?? []).findIndex(
                      (find: DataItemType) =>
                        find.namespace === item.namespace &&
                        find.filename === item.filename,
                    ) > -1) &&
                  item.availableSites.findIndex(
                    (find) => find.site_uuid === site.site_uuid,
                  ) > -1,
              ).length > 0,
          )

          /*
    // add the availability count
    */

          .map((site: Site) => {
            //
            return {
              site: site,
              count: fnD_countAvailableFiles({
                site: site,
                items: args.selectedItems ?? [],
              }),
            };
            //
          })

          /*
    // sort the sites in order of availability.
    */

          .sort((a: { site: Site; count: number }, b: { site: Site; count: number }) =>
            a.count < b.count ||
            (siteName({ site: a.site }) > siteName({ site: b.site }) &&
              a.count === b.count)
              ? 1
              : -1,
          );

  // return the site list.
  return selectedItemSites.length > 0 ? selectedItemSites : projectSites;
  //
} // getRucioSites

//  ------------------------------------------------------------
//
//  Wrapper function that handles toast messages for different
//  events
//
//  ------------------------------------------------------------

function _handleToastMessages(
  eventType: EventType,
  params: AddCollectionEvent,
  ctx?: NavigateEventContext,
) {
  //
  switch (eventType) {
    case EventType.DATA_COLLECTION_ADDED:
      ctx?.showSuccessToast?.(
        "Data collection '" +
          (params.collectionName ?? 'unknown') +
          "' added with " +
          (params.itemsAdded ?? 0).toString() +
          'item(s)',
      );
      return;

    case EventType.DATA_COLLECTION_DELETED:
      ctx?.showSuccessToast?.(
        "Data collection '" +
          (params.collectionName ?? 'unknown') +
          "' removed with " +
          (params.itemsAdded ?? 0).toString() +
          'item(s)',
      );
      return;

    case EventType.DATA_COLLECTION_ALREADY_EXISTS:
      ctx?.showWarningToast?.(
        "Data collection '" + (params.collectionName ?? 'unknown') + "' already exists",
      );
      return;

    case EventType.DATA_COLLECTION_DOES_NOT_EXIST:
      ctx?.showErrorToast?.(
        "Data collection '" + (params.collectionName ?? 'unknown') + "' does not exist",
      );
      return;

    case EventType.USER_NOT_PROJECT_MEMBER_NEW_COLLECTION:
    case EventType.USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION:
      ctx?.showErrorToast?.(
        "You are not a member of project '" + (params.projectName ?? 'unknown') + "'",
      );
      return;

    case EventType.ITEMS_ADDED_TO_DATA_COLLECTION:
      ctx?.showSuccessToast?.(
        (params.itemsAdded ?? 0).toString() +
          " item(s) added to data collection '" +
          (params.collectionName ?? 'unknown') +
          "'",
      );
      return;
  }
  //
} // _handleToastMessage

//  ------------------------------------------------------------
//
//  a data-management search is initiated, either from the Search
//  button or from a parent component.
//
//  ------------------------------------------------------------

export const initiateDMSearch = createAsyncThunk(
  'file_browser/initiateDMSearch',
  async (args: InitiateDMSearchParams, thunkAPI) => {
    //
    // fetch the state.
    let state: any = thunkAPI.getState();

    // update state variables from the new search params.
    if (args?.pageDisplayed !== undefined) {
      store.dispatch(savePageParams());
      store.dispatch(setPageDisplayed(args.pageDisplayed));
    }
    if (args?.dataset !== undefined) {
      store.dispatch(setDataset(args.dataset));
      store.dispatch(setJobID(undefined));
      store.dispatch(setCollection(undefined));
    }
    if (args?.jobID !== undefined) {
      store.dispatch(setJobID(args.jobID));
      store.dispatch(setNamespace(undefined));
      store.dispatch(setCollection(undefined));
      store.dispatch(setDataset(undefined));
    }
    if (args?.collection !== undefined) {
      store.dispatch(setCollection(args.collection));
      store.dispatch(setNamespace(undefined));
      store.dispatch(setDataset(undefined));
      store.dispatch(setJobID(undefined));
    }
    if (args?.namespace !== undefined) {
      store.dispatch(setNamespace(args.namespace));
      store.dispatch(setJobID(undefined));
      store.dispatch(setCollection(undefined));
    }
    if (args.storagePath !== undefined) {
      store.dispatch(setStoragePath(Object.assign([], args.storagePath)));
    }
    if (args.filename !== undefined) {
      const filenames: string[] = ['', args.filename];
      store.dispatch(setFilenameFilter(filenames));
    }

    // update current search parameters, and hide the details panel on the parent component.
    store.dispatch(setDisplayComputePanel(false));
    store.dispatch(setSelectedDivs([]));
    store.dispatch(setSelectedItems([]));

    // use the current timestamp as the query ID, and update state.
    const queryID: number = Date.now();
    store.dispatch(setLatestQueryID(queryID));

    // refresh the state.
    state = thunkAPI.getState();

    // display the root page.
    if (state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.FileBrowser) {
      _displayFileBrowserRoot({ focus: args.focus });
    }

    // display a list of namespaces if we're on the namespaces tab.
    if (state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.Namespaces) {
      _loadNamespaces({
        focus: args.focus,
        queryID: queryID,
      });
    }

    // display a list of collections if we're on the collections tab.
    if (state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.Collections) {
      store.dispatch(setLoadingComponent(true));
      _loadCollections({
        focus: args.focus,
        queryID: queryID,
      });
    }

    // load the data from the data-management end point of the data-management API.
    if (
      state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.Items ||
      state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.JobDetails
    ) {
      store.dispatch(setLoadingComponent(true));
      _loadData({
        focus: args.focus,
        queryID: queryID,
      });
    }

    // load the data from the storage area.
    if (
      state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.UserStorage ||
      state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.ProjectStorage
    ) {
      store.dispatch(setLoadingComponent(true));
      _loadStorage({
        focus: args.focus,
        queryID: queryID,
      });
    }

    // load files from a dataset.
    if (state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.Dataset) {
      store.dispatch(setLoadingComponent(true));
      _loadDataset({
        focus: args.focus,
        queryID: queryID,
      });
    }

    // display a list of collection items if we're on the collection tab.
    if (state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.Collection) {
      store.dispatch(setLoadingComponent(true));
      _loadCollection({
        focus: args.focus,
        queryID: queryID,
      });
    }
    //
  },
  //
); // initiateDMSearch

//  ------------------------------------------------------------
//
//  An asynchronous function that loads data from a data
//  collection stored in the gateway back end.
//
//  ------------------------------------------------------------

async function _loadCollection(args: { focus?: boolean; queryID: number }) {
  //
  // get the state.
  let state: any = store.getState();

  try {
    //
    if (state.fileBrowser.pageParams.collection !== undefined) {
      //
      var urlCommand: string =
        APIPrefix() +
        '/v1/get_data_collection_items' +
        '?collection_id=' +
        encodeURIComponent(state.fileBrowser.pageParams.collection.id.toString());

      const apiResult = await fetch(urlCommand, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        const returnedJson = await apiResult.json();

        // refresh the state.
        state = store.getState();
        if (state.fileBrowser.latestQueryID === args.queryID) {
          //
          // get collection list.
          var dataList: DataItemType[] = [];
          if (returnedJson.data_collection_items !== undefined)
            for (var i = 0; i < returnedJson.data_collection_items.length; i++) {
              const id: number = returnedJson.data_collection_items[i].item_id;
              const namespace: string = returnedJson.data_collection_items[i].namespace;
              const filename: string = returnedJson.data_collection_items[i].filename;

              const collection: DataItemType = {
                id: id,
                namespace: namespace,
                filename: filename,
                did_type: 'file',
                bytes: -1,
              };
              var addItem: boolean = true;
              if (filenameFilter() !== '')
                if (filename.indexOf(filenameFilter()) === -1) {
                  addItem = false;
                }
              if (state.fileBrowser.pageParams.fileTypeFilter !== undefined)
                if (
                  state.fileBrowser.pageParams.fileTypeFilter !== 'all' &&
                  returnedJson.data_collection_items[i].did_type.toUpperCase() !==
                    state.fileBrowser.pageParams.fileTypeFilter.toUpperCase()
                )
                  addItem = false;

              if (addItem === true) {
                dataList.push(collection);
              }
            }

          // update the state.
          console.log(dataList);
          store.dispatch(setDataList(dataList));

          // update the focus trigger if we need to be setting the focus.
          if (args.focus === true) {
            store.dispatch(setFocusElement({ divID: 0 }));
          }
          //
        }
        //
      }
      //
    }
    //
  } catch (e) {
  } finally {
    //
    // check if the query ID is correct, or if the query has been cancelled.
    state = store.getState();
    if (state.fileBrowser.latestQueryID === args.queryID) {
      //
      // turn off the loading flag, and reset the query ID.
      store.dispatch(setLoadingComponent(false));
      store.dispatch(setLatestQueryID(-1));
      //
    }
    //
  }
  //
} // _loadCollection

//  ------------------------------------------------------------
//
//  An asynchronous function that loads the collection data
//  from the back end.
//
//  ------------------------------------------------------------

async function _loadCollections(args: { focus?: boolean; queryID: number }) {
  //
  // get the state.
  let state: any = store.getState();

  let filename: string = filenameFilter();

  try {
    //
    if ((state.global.project?.name ?? '') !== '') {
      //
      var urlCommand: string =
        APIPrefix() +
        '/v1/get_data_collections' +
        '?project=' +
        encodeURIComponent(state.global.project?.name ?? '');

      const apiResult = await fetch(urlCommand, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        const returnedJson = await apiResult.json();

        // refresh the state.
        state = store.getState();
        if (state.fileBrowser.latestQueryID === args.queryID) {
          //
          // get collection list.
          var dataList: DataItemType[] = [];
          if (returnedJson.data_collections !== undefined)
            for (var i = 0; i < returnedJson.data_collections.length; i++) {
              const name: string = returnedJson.data_collections[i].name;
              const collection: DataItemType = {
                id: returnedJson.data_collections[i].collection_id,
                namespace: '',
                filename: name,
                did_type: 'collection',
                bytes: -1,
              };
              var addItem: boolean = true;
              if (filename !== undefined) {
                if (name.indexOf(filename) === -1) {
                  addItem = false;
                }
              }
              if (addItem === true) {
                dataList.push(collection);
              }
            }

          // update the state.
          store.dispatch(setDataList(dataList));

          // update the focus trigger if we need to be setting the focus.
          if (args.focus === true) {
            store.dispatch(setFocusElement({ divID: 0 }));
          }
          //
        }
        //
      }
      //
    }
    //
  } catch (e) {
  } finally {
    //
    // check if the query ID is correct, or if the query has been cancelled.
    state = store.getState();
    if (state.fileBrowser.latestQueryID === args.queryID) {
      //
      // turn off the loading flag, and reset the query ID.
      store.dispatch(setLoadingComponent(false));
      store.dispatch(setLatestQueryID(-1));
      //
    }
    //
  }
  //
} // _loadCollections

//  ------------------------------------------------------------
//
//  An asynchronous function that loads data from the data
//  management API for a specific namespace.
//
//  ------------------------------------------------------------

async function _loadData(args: { focus?: boolean; queryID: number }) {
  //
  let state: any = store.getState();

  var urlCommand: string = APIPrefix() + '/v1/data_management/list_data?';
  if ((state.fileBrowser.pageParams.namespace ?? '') !== '')
    urlCommand =
      urlCommand +
      'namespace=' +
      encodeURIComponent(state.fileBrowser.pageParams.namespace ?? '') +
      '&';
  if (filenameFilter() !== '')
    urlCommand = urlCommand + 'filename=' + encodeURIComponent(filenameFilter()) + '&';
  if ((state.fileBrowser.pageParams.jobID ?? '') !== '')
    urlCommand =
      urlCommand +
      'job_id=' +
      encodeURIComponent(state.fileBrowser.pageParams.jobID ?? '') +
      '&';
  urlCommand = urlCommand.slice(0, -1);

  try {
    //
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();

      // check if the query ID is correct, or if the query has been cancelled.
      state = store.getState();
      if (state.fileBrowser.latestQueryID === args.queryID) {
        //
        store.dispatch(setLoadingComponent(false));
        store.dispatch(setLatestQueryID(-1));

        // get data list.
        var dataList: DataItemType[] = [];
        if (returnedJson.data_list !== undefined)
          for (var i: number = 0; i < returnedJson.data_list.length; i++) {
            const item: DataItemType = {
              id: -1,
              namespace: returnedJson.data_list[i].scope,
              filename: returnedJson.data_list[i].name,
              did_type: returnedJson.data_list[i].did_type,
              bytes: returnedJson.data_list[i].bytes,
            };

            var addItem: boolean = true;
            if (state.fileBrowser.pageParams.fileTypeFilter !== undefined)
              if (
                state.fileBrowser.pageParams.fileTypeFilter !== 'all' &&
                item.did_type?.toUpperCase() !==
                  state.fileBrowser.pageParams.fileTypeFilter.toUpperCase()
              )
                addItem = false;

            if (addItem === true) {
              dataList.push(item);
            }
          }

        // update the state with the list of returned data items.
        store.dispatch(setDataList(dataList));

        // update the focus trigger if we need to be setting the focus.
        if (args.focus === true) {
          store.dispatch(setFocusElement({ divID: 0 }));
        }
        //
      }
      //
    } else {
      //
      // check if the query ID is correct, or if the query has been cancelled.
      state = store.getState();
      if (state.fileBrowser.latestQueryID === args.queryID) {
        store.dispatch(setDataList([]));
      }
      //
    }
    //
  } catch (e) {
    console.log(e);
  } finally {
    //
    // check if the query ID is correct, or if the query has been cancelled.
    state = store.getState();
    if (state.fileBrowser.latestQueryID === args.queryID) {
      //
      // turn off the loading flag, and reset the query ID.
      store.dispatch(setLoadingComponent(false));
      store.dispatch(setLatestQueryID(-1));
      //
    }
    //
  }
  //
} // _loadData

//  ------------------------------------------------------------
//
//  An asynchronous function that loads data from the data
//  management API for a specific dataset.
//
//  ------------------------------------------------------------

async function _loadDataset(args: { focus?: boolean; queryID: number }) {
  //
  // get the state.
  let state: any = store.getState();

  // get storage area IDs
  let storageAreas: {
    identifier: string;
    associated_storage_area_id: string;
    replicas: string[];
  }[] = await store
    .dispatch(
      fnD_getAvailableLocations({
        items: [
          {
            namespace: state.fileBrowser.pageParams.namespace ?? '',
            filename: state.fileBrowser.pageParams.dataset ?? '',
          },
        ],
      }),
    )
    .unwrap();

  // refresh the state, and only proceed if the query ID is the latest issued.
  state = store.getState();
  if (state.fileBrowser.latestQueryID === args.queryID) {
    //
    // get data list.
    var dataList: DataItemType[] = [];
    if (storageAreas.length > 0)
      for (var i = 0; i < storageAreas[0].replicas.length; i++) {
        //
        // extract the filename after the last /.
        var filename: string = storageAreas[0].replicas[i];
        if (filename.lastIndexOf('/') > -1)
          filename = filename.slice(-(filename.length - filename.lastIndexOf('/') - 1));

        // check if filename matches the filter.
        var matchesFilter: boolean = true;
        if (filenameFilter() !== undefined)
          matchesFilter = filename.indexOf(filenameFilter()) > -1;

        // if we are filtering by file type, then remove the non-matches.
        if (state.fileBrowser.pageParams.fileTypeFilter !== undefined)
          if (
            state.fileBrowser.pageParams.fileTypeFilter.toUpperCase() === 'DATASET' ||
            state.fileBrowser.pageParams.fileTypeFilter.toUpperCase() === 'CONTAINER'
          )
            matchesFilter = false;

        // push the new data item.
        if (matchesFilter === true) {
          var dataItem: DataItemType = {
            id: -1,
            namespace: state.fileBrowser.pageParams.namespace ?? '',
            filename: filename,
            did_type: 'file',
            bytes: 0,
          };
          dataList.push(dataItem);
        }
        //
      }

    // update the state with the list of returned data items.
    store.dispatch(setDataList(dataList));

    // update the focus trigger if we need to be setting the focus.
    if (args.focus === true) {
      store.dispatch(setFocusElement({ divID: 0 }));
    }

    store.dispatch(setLoadingComponent(false));
    store.dispatch(setLatestQueryID(-1));
    //
  }
  //
} // _loadDataset

//  ------------------------------------------------------------
//
//  An asynchronous function that loads the namespace data
//  from the data-management API.
//
//  ------------------------------------------------------------

async function _loadNamespaces(args: { focus?: boolean; queryID: number }) {
  //
  var urlCommand: string = APIPrefix() + '/v1/data_management/list_namespaces';

  try {
    //
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Return code 200 means the API has run.
    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();

      // fetch state, and only proceed if the query IDs match.
      let state: any = store.getState();
      if (state.fileBrowser.latestQueryID === args.queryID) {
        //
        // get namespaces list.
        var namespaceList: any = [];
        if (returnedJson.namespaces !== undefined) {
          namespaceList = returnedJson.namespaces;
        }

        // display the namespace list.
        _displayNamespaces({
          namespaces: namespaceList,
          filename:
            state.fileBrowser.pageParams.filenameFilter[
              state.fileBrowser.pageParams.filenameFilter.length - 1
            ],
        });

        // update the focus trigger if we need to be setting the focus.
        if (args.focus === true) {
          store.dispatch(setFocusElement({ divID: 0 }));
        }
        //
      }
      //
    }

    // HTTP 401 means the session has expired.
    else if (apiResult.status === 401) {
      console.log('loadNamespaces 401');
    }
    //
  } catch (e) {
  } finally {
    //
    // check if the query ID is correct, or if the query has been cancelled.
    let state: any = store.getState();
    if (state.fileBrowser.latestQueryID === args.queryID) {
      store.dispatch(setLoadingComponent(false));
      store.dispatch(setLatestQueryID(-1));
    }
    //
  }
  //
} // _loadNamespaces

//  ------------------------------------------------------------
//
//  An asynchronous function that loads data from a data
//  collection stored in the gateway back end.
//
//  ------------------------------------------------------------

async function _loadStorage(args: { focus?: boolean; queryID: number }) {
  //
  // fetch state.
  let state: any = store.getState();

  // Build storage path
  const pathSegments: string[] =
    state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.UserStorage ||
    state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.ProjectStorage
      ? state.fileBrowser.pageParams.storagePath
      : [];
  const path = pathSegments?.join('/') + (pathSegments?.length > 0 ? '/' : '');

  const url = state.global.selectedCanfar?.url ?? '';

  // Build query params
  const params = new URLSearchParams();
  params.append(
    'storage_type',
    state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.UserStorage
      ? 'user'
      : 'project',
  );
  if (url !== '') {
    params.append('url', url);
  }

  if (state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.UserStorage) {
    params.append('path', `/home/${state.global.preferredUsername}/${path}`);
  } else if (
    state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.ProjectStorage
  ) {
    const projectName = state.global.project?.name ?? 'unknown';
    params.append('path', `/projects/${projectName}/${path}`);
  }

  const urlCommand = `${APIPrefix()}/v1/storage/list_items?${params.toString()}`;
  const $fetch = createFetch({
    retry: {
      type: 'linear',
      attempts: 3,
      delay: 1000,
      retryOn: (error: unknown, res: Response | undefined) => !res || res.status === 504,
      resolveBodyOnly: false,
    },
  });

  try {
    //
    const { data, error } = await $fetch<
      {
        vosUri?: string;
        items?: {
          filename: string;
          file_type: string;
          length: number;
        }[];
      },
      ApiError
    >(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      errorSchema: ApiErrorSchema,
    });

    // check if the query ID is correct, or if the query has been cancelled.
    state = store.getState();
    if (state.fileBrowser.latestQueryID !== args.queryID) {
      return;
    }

    store.dispatch(setLoadingComponent(false));
    store.dispatch(setLatestQueryID(-1));

    if (error) {
      console.warn('!200 response:', data, error);
      store.dispatch(setDataList([]));
      console.warn('Error message:', JSON.stringify(error.detail));
      const message = _extractErrorMessage(error);

      // No node found
      if (message.includes('NodeNotFound:')) {
        store.dispatch(
          setErrorMessage(
            `Folder not found. Please ensure the correct storage location ${state.fileBrowser.pageParams.pageDisplayed === FileBrowserPage.ProjectStorage ? 'or project' : ''} has been selected`,
          ),
        );
      }
      // No data items found
      else {
        store.dispatch(setErrorMessage(message));
      }
    }

    if (data?.vosUri) {
      store.dispatch(setVosUri(data.vosUri));
    }

    const filename: string = filenameFilter();

    const items: DataItemType[] =
      data?.items
        ?.filter((item: any) => {
          const isMatch =
            (!filename || item.filename.includes(filename)) &&
            (state.fileBrowser.pageParams.showHiddenFiles ||
              !item.filename.startsWith('.')) &&
            (state.fileBrowser.pageParams.fileTypeFilter.toUpperCase() === 'ALL' ||
              !state.fileBrowser.pageParams.fileTypeFilter ||
              item.file_type.toUpperCase() ===
                state.fileBrowser.pageParams.fileTypeFilter.toUpperCase());

          return isMatch;
        })
        .map((item: any) => ({
          id: -1,
          namespace: '',
          filename: item.filename,
          did_type: item.file_type,
          bytes: item.length,
        })) ?? [];

    store.dispatch(setDataList(items));

    // update the focus trigger if we need to be setting the focus.
    if (args.focus === true) {
      store.dispatch(setFocusElement({ divID: 0 }));
    }
    //
  } catch (e) {
    //
    // check if the query ID is correct, or if the query has been cancelled.
    state = store.getState();
    if (state.fileBrowser.latestQueryID !== args.queryID) {
      return;
    }

    store.dispatch(setLoadingComponent(false));
    store.dispatch(setLatestQueryID(-1));

    console.error('Error loading storage:', e);
    store.dispatch(setDataList([]));
    store.dispatch(setErrorMessage('Unexpected Error occured. Please try again.'));
    //
  }
  //
} // _loadStorage

//  ------------------------------------------------------------
//
//  An asynchronous function that moves files from one location
//  to another in user/project storage.
//
//  ------------------------------------------------------------

export const moveFiles = createAsyncThunk(
  'file_browser/moveFiles',
  async (
    args: {
      fromPath: string[];
      filenames: string[];
      toPath: string[];
      storageType: 'user' | 'project';
    },
    thunkAPI,
  ) => {
    //
    const stateFetch: any = thunkAPI.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;
    const globalState: GlobalState = stateFetch.global;

    console.log('moveFiles fires:');

    const { fromPath, toPath, filenames, storageType } = args;

    const fromPathStr = fromPath?.join('/') + '/';
    const toPathStr = toPath?.join('/') + '/';

    // Prevent same-path operations
    if (fromPathStr === toPathStr) {
      console.warn('From and To paths are identical. Cancelling move.');
      return;
    }

    // Resolve base URL
    const url = globalState.selectedCanfar?.url || '';

    // Resolve base storage prefix
    let basePrefix = '';
    if (storageType === 'user') {
      const username = globalState.preferredUsername || '';
      if (!username) {
        console.error('Username missing for user storage');
        return;
      }
      basePrefix = `/home/${username}/`;
    } else if (storageType === 'project') {
      basePrefix = `/projects/${globalState.project?.name}/`;
    }

    for (let i = 0; i < filenames.length; i++) {
      const filename = filenames[i];
      const fullFromPath = `${basePrefix}${fromPathStr}/${filename}`;
      const fullToPath = `${basePrefix}${toPathStr}/`;

      console.log('From path>>' + fullFromPath);
      console.log('To path>>' + fullToPath);
      const urlCommand =
        `${APIPrefix()}/v1/storage/move_file` +
        `?url=${encodeURIComponent(url)}` +
        `&from_path=${encodeURIComponent(fullFromPath)}` +
        `&to_path=${encodeURIComponent(fullToPath)}`;

      try {
        const apiResult = await fetch(urlCommand, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (apiResult.ok) {
          refreshPage();
        } else {
          let rawJson = await apiResult.text();
          const parsedJson = JSON.parse(rawJson, (key, value) => {
            return value;
          });
          console.log('Failed to move file(s):', parsedJson.detail);
          /*if (parsedJson.detail.includes('403 Forbidden')) {
            refreshPage();
            toast.error('Request forbidden by administrative rules.', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: false,
              progress: undefined,
              theme: globalState.darkMode ? 'dark' : 'light',
            });
          }*/ // CJS: temporarily removed
        }
      } catch (e) {
        console.error(`Move failed for ${filename}:`, e);
      }
    }
  },
  //
); // moveFiles

//  ------------------------------------------------------------
//
//  handle the click event for a context menu item by launching
//  the move-data process.
//
//  ------------------------------------------------------------

export function onContextMenuClick(args: { menuID: number; rowID: number[] }) {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;
  const contextState: ContextMenuState = stateFetch.contextMenu;
  const globalState: GlobalState = stateFetch.globalState;
  const dataState: DataState = stateFetch.data;

  // if we've clicked on a storage area on the storages menu
  // then launch the move-data process.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER.STORAGES) {
    //
    // build a list of selected files.
    const filesToMove: {
      namespace: string;
      filename: string;
      filetype: string;
      bytes: number;
    }[] = [];
    for (
      var selectedItemIdx = 0;
      selectedItemIdx < state.selectedDivs.length;
      selectedItemIdx++
    ) {
      // is the selected item is a data collection?
      if (
        state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ===
        'COLLECTION'
      )
        filesToMove.push({
          namespace: '{COLLECTION_ID}',
          filename:
            state.dataList[state.selectedDivs[selectedItemIdx]].id?.toString() ?? '-1',
          filetype: 'COLLECTION',
          bytes: 0,
        });

      // is the selected item a file?
      if (
        state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ===
          'FILE' ||
        state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ===
          'DATASET' ||
        state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ===
          'CONTAINER'
      )
        filesToMove.push({
          namespace: state.dataList[state.selectedDivs[selectedItemIdx]].namespace,
          filename: state.dataList[state.selectedDivs[selectedItemIdx]].filename,
          filetype:
            state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ??
            '',
          bytes: state.dataList[state.selectedDivs[selectedItemIdx]].bytes ?? 0,
        });
    }

    // moving data to a storage location.
    const moveDataToStorage: MoveDataToStorage = {
      toStorageAreaUUID:
        dataState.storageAreas[args.rowID[CONTEXT_MENU_FILE_BROWSER.SITES]].storage_areas[
          args.rowID[CONTEXT_MENU_FILE_BROWSER.STORAGES]
        ].storage_id,
      toSiteUUID:
        dataState.storageAreas[args.rowID[CONTEXT_MENU_FILE_BROWSER.SITES]].site
          .site_uuid ?? '',
      toStorageIdentifier:
        dataState.storageAreas[args.rowID[CONTEXT_MENU_FILE_BROWSER.SITES]].storage_areas[
          args.rowID[CONTEXT_MENU_FILE_BROWSER.STORAGES]
        ].name,
      lifetime: state.lifetime,
      filesToMove: filesToMove,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.MOVE_DATA_TO_STORAGE,
      parameters: moveDataToStorage,
    };
    store.dispatch(handleEvent({ event: newEvent }));

    store.dispatch(rG_setSubmittingJob(true));
    //
  }

  // if we've clicked to remove a data collection.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-deletecollection'
  ) {
    // build a list of selected files.
    const collectionIDsToRemove: number[] = [];
    for (
      var selectedItemIdx = 0;
      selectedItemIdx < state.selectedDivs.length;
      selectedItemIdx++
    ) {
      // is the selected item a file?
      if (
        state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ===
        'COLLECTION'
      )
        collectionIDsToRemove.push(
          state.dataList[state.selectedDivs[selectedItemIdx]].id ?? -1,
        );
    }

    // delete the data collection.
    const removeDataCollection: DataCollection = {
      dataCollection: state.pageParams.collection,
      project: globalState.project,
      collectionIDs: collectionIDsToRemove,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.DELETE_DATA_COLLECTION,
      parameters: removeDataCollection,
    };
    store.dispatch(handleEvent({ event: newEvent }));
  }

  // if we've clicked to remove an item(s) from a data collection.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-removeitemfromcollection'
  ) {
    // build a list of selected files.
    const filesToRemove: {
      namespace: string;
      filename: string;
    }[] = [];
    for (
      var selectedItemIdx = 0;
      selectedItemIdx < state.selectedDivs.length;
      selectedItemIdx++
    ) {
      // is the selected item a file?
      if (
        state.dataList[state.selectedDivs[selectedItemIdx]].did_type?.toUpperCase() ===
        'FILE'
      )
        filesToRemove.push({
          namespace: state.dataList[state.selectedDivs[selectedItemIdx]].namespace,
          filename: state.dataList[state.selectedDivs[selectedItemIdx]].filename,
        });
    }

    // remove items from the data collection.
    const removeFromDataCollection: DataCollection = {
      dataCollection: state.pageParams.collection,
      project: globalState.project,
      dids: filesToRemove,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.REMOVE_FROM_DATA_COLLECTION,
      parameters: removeFromDataCollection,
    };
    store.dispatch(handleEvent({ event: newEvent }));
  }

  // if we've clicked to add a new directory the user storage.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-createfolder' &&
    state.renameIndex === -1
  ) {
    addTemporaryItem();

    // disable cutting
    store.dispatch(setClipboard([]));
  }

  // if we've clicked to rename an item in the user storage.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-renamefile'
  ) {
    store.dispatch(setRenameIndex(state.selectedDivs[0]));
    store.dispatch(setRenameType(RenameType.EXISTING));
    store.dispatch(setRenameFilename(state.dataList[state.selectedDivs[0]].filename));

    // disable cutting
    store.dispatch(setClipboard([]));
  }

  // if we've clicked to delete an item in the user storage.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-deletenode'
  ) {
    deleteFile({
      filename: state.dataList[state.selectedDivs[0]].filename,
      storageType:
        state.pageParams.pageDisplayed === FileBrowserPage.UserStorage
          ? 'user'
          : 'project',
    });
  }

  // if we've clicked to cut the current item.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-cut'
  ) {
    // put the currently selected items onto the clipboard.
    var clipboard: string[] = [];
    for (var i: number = 0; i < state.selectedDivs.length; i++) {
      clipboard.push(state.dataList[state.selectedDivs[i]].filename);
    }
    store.dispatch(setClipboard(clipboard));
    store.dispatch(setClipboardPath(state.pageParams.storagePath.slice()));
    store.dispatch(setInClipboardFolder(true));

    // disable renaming
    store.dispatch(setRenameIndex(-1));
  }

  // if we've clicked to paste the current line.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-paste'
  ) {
    moveFiles({
      fromPath: state.clipboardPath,
      filenames: state.clipboard,
      toPath: state.pageParams.storagePath,
      storageType:
        state.pageParams.pageDisplayed === FileBrowserPage.UserStorage
          ? 'user'
          : 'project',
    });
    store.dispatch(setClipboard([]));
    store.dispatch(setClipboardPath([]));
    store.dispatch(setInClipboardFolder(false));
    store.dispatch(setSelectedDivs([]));
    store.dispatch(setSelectedItems([]));
  }

  // if we've clicked to upload a file.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-upload'
  ) {
    // open the file dialog.
    //fileDialogUpload?.current?.click();
    store.dispatch(setOpenFileDialogTrigger(Date.now()));
  }

  // if we've clicked to download a file.
  if (
    contextState.contextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][args.menuID].items[
      args.rowID[args.menuID]
    ].id === 'mnu-main-download'
  ) {
    // open the file dialog.
    //fileDialogDownload?.current?.click();
    downloadFile({
      file: state.dataList[state.selectedDivs[0]],
      storageType:
        state.pageParams.pageDisplayed === FileBrowserPage.UserStorage
          ? 'user'
          : 'project',
    });
  }
  //
} // onContextMenuClick

//  ------------------------------------------------------------
//
//  handle the click event for a context menu item.
//
//  ------------------------------------------------------------

export function onContextMenuClickFileBrowserDataFiles(args: {
  menuID: CONTEXT_MENU_FILE_BROWSER_STAGING;
  rowID: number[];
}) {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;
  const stateData: DataState = stateFetch.data;

  // if we've clicked on a metadata item on the meta data menu
  // then display that item.
  if (args.menuID === CONTEXT_MENU_FILE_BROWSER_STAGING.STORAGES) {
    //
    // build a list of selected files.
    let filesToMove: DataItemType[] = [];

    // add the selected items.
    if (state.selectedItems.length > 0) {
      filesToMove = state.selectedItems.map((map) => {
        return {
          namespace: map.namespace,
          filename: map.filename,
          did_type: 'FILE',
          bytes: 0,
        };
      });
    }

    const siteIndex: number = args.rowID[CONTEXT_MENU_FILE_BROWSER_STAGING.SITES];
    const storageAreaIndex: number =
      args.rowID[CONTEXT_MENU_FILE_BROWSER_STAGING.STORAGES];

    // moving data to a storage location.
    const moveDataToStorage: MoveDataToStorage = {
      toStorageAreaUUID:
        stateData.storageAreas[siteIndex].storage_areas[storageAreaIndex].storage_id,
      toSiteUUID: stateData.storageAreas[siteIndex].site.site_uuid ?? '',
      toStorageIdentifier:
        stateData.storageAreas[siteIndex].storage_areas[storageAreaIndex].name,
      lifetime: 3600,
      filesToMove: filesToMove,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.MOVE_DATA_TO_STORAGE,
      parameters: moveDataToStorage,
    };
    store.dispatch(handleEvent({ event: newEvent }));

    //store.dispatch(setSubmittingJob(true));
    //
  }
  //
} // onContextMenuClickFileBrowserDataFiles

//  ------------------------------------------------------------
//
//  refresh the page
//
//  ------------------------------------------------------------

export function refreshPage() {
  //
  const stateFetch: any = store.getState();
  const state: FileBrowserState = stateFetch.fileBrowser;
  const globalState: GlobalState = stateFetch.global;

  // run the data-management search.
  const params: InitiateDMSearchParams = {
    namespace: state.pageParams.namespace,
    filename: filenameFilter(),
    jobID: state.pageParams.jobID,
    dataset: state.pageParams.dataset,
    project: globalState.project,
    collection: state.pageParams.collection,
    preferredUsername: globalState.preferredUsername,
    storagePath: state.pageParams.storagePath,
    canfarService: globalState.selectedCanfar,
    showHiddenFiles: state.pageParams.showHiddenFiles,
    fileType: state.pageParams.fileTypeFilter,
    pageDisplayed: state.pageParams.pageDisplayed,
    storageType:
      state.pageParams.pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
  };
  const newEvent: Event = {
    eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
    eventType: EventType.INITIATE_DM_SEARCH,
    parameters: params,
  };
  store.dispatch(handleEvent({ event: newEvent }));
  //
} // refreshPage
